import React from "react";
import logoMombasa from "../../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../../core/img/branch-logos/logo_texastrack.png";
import { TYPE_LABEL, dayLabel, entryDateLabel } from "../ledgerGrouping";

// Same 4 branch letterheads and MOMBASA fallback as the voucher print and the
// stock documents, so the ledger prints on the same paper as everything else.
const LOGO_BY_BRANCH = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

const amount = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const amountOrDash = (v) => (Number(v) ? amount(v) : "-");
const fullDate = (v) =>
  new Date(`${v}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

/**
 * Printable petty cash ledger - one section per fund: a period summary
 * (opening, debit, credit, closing), then every day newest-first with its
 * opening line, entries and closing line carrying the day's own totals, then
 * sign-off lines for whoever checks the book.
 *
 * Rendered both in the preview modal and, via renderToStaticMarkup, into the
 * print popup - one template, not two, so everything is inline-styled (the
 * popup has no stylesheet). Same approach as VoucherPrintPreview.
 */
// `flat` (2026-09-22): the fund's flatRows in voucher / receipt number order,
// entries only - the day sections and opening / closing lines are date-order
// concepts. The period summary at the top still holds either way.
const LedgerPrintPreview = ({ fundGroups, dateFrom, dateTo, companyName, flat = false, orderLabel }) => {
  const th = {
    padding: "5px 6px",
    fontSize: "10.5px",
    textAlign: "left",
    borderBottom: "1.5px solid #000",
    background: "#f0f0f0",
  };
  const td = { padding: "4px 6px", fontSize: "10.5px", borderBottom: "1px solid #ddd", verticalAlign: "top" };
  const num = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
  const numTh = { ...th, textAlign: "right" };
  const line = { borderBottom: "1px solid #000", height: "30px" };

  return (
    <div
      style={{
        width: "190mm",
        margin: "0 auto",
        padding: "10mm",
        background: "#fff",
        color: "#000",
        fontFamily: "Arial, Helvetica, sans-serif",
        boxSizing: "border-box",
      }}
    >
      {fundGroups.map((fund, fundIndex) => (
        <div key={fund.fundId} style={fundIndex > 0 ? { pageBreakBefore: "always", breakBefore: "page" } : undefined}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <img src={LOGO_BY_BRANCH[fund.branchCode] || logoMombasa} alt="" style={{ height: "56px", objectFit: "contain" }} />
            <div style={{ textAlign: "right" }}>
              {companyName && <div style={{ fontSize: "12px", fontWeight: 700 }}>{companyName}</div>}
              <div style={{ fontSize: "16px", fontWeight: 700, letterSpacing: "1px" }}>PETTY CASH LEDGER</div>
              <div style={{ fontSize: "12px", marginTop: "2px" }}>
                {fund.fundName} <span style={{ color: "#555" }}>({fund.fundCode} · {fund.branchCode})</span>
              </div>
              <div style={{ fontSize: "11px", color: "#555" }}>
                {fullDate(dateFrom)} to {fullDate(dateTo)}
                {orderLabel ? ` · sorted by ${orderLabel}` : ""}
              </div>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", border: "1px solid #000" }}>
            <tbody>
              <tr>
                {[
                  ["Opening balance", fund.periodOpening],
                  ["Total debit (paid out)", fund.totalDebit],
                  ["Total credit (received)", fund.totalCredit],
                  ["Closing balance", fund.periodClosing],
                ].map(([label, value], i) => (
                  <td key={label} style={{ padding: "6px 8px", borderLeft: i ? "1px solid #000" : undefined, width: "25%" }}>
                    <div style={{ fontSize: "9.5px", color: "#555", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
                    <div style={{ fontSize: "13px", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{amount(value)}</div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
            <thead style={{ display: "table-header-group" }}>
              <tr>
                <th style={{ ...th, width: "78px" }}>Date</th>
                <th style={th}>Description</th>
                <th style={numTh}>Debit</th>
                <th style={numTh}>Credit</th>
                <th style={numTh}>Balance</th>
                <th style={th}>Type</th>
                <th style={th}>Reference</th>
                <th style={th}>Receipt No</th>
              </tr>
            </thead>
            <tbody>
              {flat &&
                (fund.flatRows || []).map((r) => (
                  <tr key={r.ledgerId} style={{ pageBreakInside: "avoid" }}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{entryDateLabel(r.transactionDate)}</td>
                    <td style={td}>{r.description || "-"}</td>
                    <td style={num}>{amountOrDash(r.debitAmount)}</td>
                    <td style={num}>{amountOrDash(r.creditAmount)}</td>
                    <td style={num}>{amount(r.balanceAfterTransaction)}</td>
                    <td style={td}>{TYPE_LABEL[r.transactionType] || r.transactionType}</td>
                    <td style={td}>{r.referenceNumber || "-"}</td>
                    <td style={td}>{r.receiptNumber || "-"}</td>
                  </tr>
                ))}
              {!flat && fund.days.map((day) => (
                <React.Fragment key={day.key}>
                  <tr style={{ pageBreakInside: "avoid" }}>
                    <td colSpan={8} style={{ ...td, fontWeight: 700, background: "#f7f7f7", borderBottom: "1px solid #999", paddingTop: "7px" }}>
                      {dayLabel(day.date)}
                    </td>
                  </tr>
                  <tr style={{ fontStyle: "italic", color: "#555" }}>
                    <td style={td} />
                    <td style={td}>Opening Balance</td>
                    <td style={num} />
                    <td style={num} />
                    <td style={num}>{amount(day.openingBalance)}</td>
                    <td style={td} />
                    <td style={td} />
                    <td style={td} />
                  </tr>
                  {day.rows.map((r) => (
                    <tr key={r.ledgerId} style={{ pageBreakInside: "avoid" }}>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{entryDateLabel(r.transactionDate)}</td>
                      <td style={td}>{r.description || "-"}</td>
                      <td style={num}>{amountOrDash(r.debitAmount)}</td>
                      <td style={num}>{amountOrDash(r.creditAmount)}</td>
                      <td style={num}>{amount(r.balanceAfterTransaction)}</td>
                      <td style={td}>{TYPE_LABEL[r.transactionType] || r.transactionType}</td>
                      <td style={td}>{r.referenceNumber || "-"}</td>
                      <td style={td}>{r.receiptNumber || "-"}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700 }}>
                    <td style={{ ...td, borderBottom: "1.5px solid #000" }} />
                    <td style={{ ...td, borderBottom: "1.5px solid #000" }}>
                      Closing Balance <span style={{ fontWeight: 400, color: "#555" }}>(day total)</span>
                    </td>
                    <td style={{ ...num, borderBottom: "1.5px solid #000" }}>{amountOrDash(day.totalDebit)}</td>
                    <td style={{ ...num, borderBottom: "1.5px solid #000" }}>{amountOrDash(day.totalCredit)}</td>
                    <td style={{ ...num, borderBottom: "1.5px solid #000" }}>{amount(day.closingBalance)}</td>
                    <td style={{ ...td, borderBottom: "1.5px solid #000" }} />
                    <td style={{ ...td, borderBottom: "1.5px solid #000" }} />
                    <td style={{ ...td, borderBottom: "1.5px solid #000" }} />
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Signed by hand when the book is checked - a ledger printout is
              usually the custodian handing the period over for review. */}
          <div style={{ display: "flex", gap: "18px", marginTop: "24px", pageBreakInside: "avoid" }}>
            {["Prepared by (custodian)", "Checked by", "Approved by"].map((sig) => (
              <div key={sig} style={{ flex: 1 }}>
                <div style={line} />
                <div style={{ fontSize: "10px", color: "#555", marginTop: "3px" }}>{sig}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "12px", fontSize: "9px", color: "#777", textAlign: "center" }}>
            {fund.fundName} · {fund.entryCount} entr{fund.entryCount === 1 ? "y" : "ies"} · printed{" "}
            {new Date().toLocaleString("en-GB")}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LedgerPrintPreview;
